import React, { useState } from 'react';
import moment from 'moment';
import {
    DateParam,
    JsonParam,
    NumberParam,
    useQueryParams,
    withDefault,
} from 'use-query-params';
import {
    Grid,
    Button,
    TextField,
    Stack,
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import { DatePicker } from '@mui/x-date-pickers';

import { Customer, Employee, Point } from '@/types';
import { useAuth, useDebounce } from '@/hooks';
import {
    AppBar,
    DownloadFile,
    Empty,
    Error,
    Loader,
} from '@/components';
import { SearchPoints } from '@/pages/Points/components';
import { SearchCustomers } from '@/pages/Customers/components';
import { SearchEmployees } from '@/pages/Employees/components';
import { ChartGrowthFlat } from '@/assets/icons';

import {
    CashboxOperationsSummaryTable,
    CashboxOperationsTable,
} from './components';
import {
    useLazyGetCashboxOperationsReportQuery,
    useLazyGetCashboxOperationsSummaryReportQuery
} from "@/services/reports";

const CashboxOperationsReportPage: React.FC = () => {
    const [
        { page, points, customers, employees, date_from, date_to },
        setQuery,
    ] = useQueryParams({
        page: withDefault(NumberParam, 1),
        points: withDefault<Partial<Point>[], Point[]>(JsonParam, []),
        customers: withDefault<Partial<Customer>[], Customer[]>(JsonParam, []),
        employees: withDefault<Partial<Employee>[], Employee[]>(JsonParam, []),
        date_from: withDefault(DateParam, null),
        date_to: withDefault(DateParam, moment().toDate()),
    });

    const { user, isAdmin, isRevisor, isCashier } = useAuth();

    const point_ids = useDebounce(points.map(p => p.id));
    const customer_ids = useDebounce(customers.map(c => c.id));
    const employee_ids = useDebounce(employees.map(e => e.id));

    const params = {
        point_ids: isCashier ? [user?.point?.id] : point_ids,
        customer_ids,
        employee_ids,
        date_from: date_from ? moment(date_from).format('YYYY-MM-DD') : null,
        date_to: moment(date_to).format('YYYY-MM-DD'),
    };

    const emptyFilters =
        (isAdmin || isRevisor) &&
        ![point_ids.length, customer_ids.length, employee_ids.length].some(Boolean);

    const [hasSearched, setHasSearched] = useState(false);

    // lazy hooks
    const [
        triggerSummary,
        {
            data: summaryData,
            isLoading: loadingSummary,
            isFetching: fetchingSummary,
            isError: errorSummary,
            isSuccess: successSummary,
        },
    ] = useLazyGetCashboxOperationsSummaryReportQuery();

    const [
        triggerReport,
        {
            data: reportData,
            isLoading: loadingReport,
            isFetching: fetchingReport,
            isError: errorReport,
            isSuccess: successReport,
        },
    ] = useLazyGetCashboxOperationsReportQuery();

    const isLoading = loadingSummary || loadingReport;
    const isFetching = fetchingSummary || fetchingReport;

    const onSearch = () => {
        if (emptyFilters) return;
        setQuery({ page: 1 });
        setHasSearched(true);
        triggerSummary({ params });
        triggerReport({ params: { ...params, page: 1 } });
    };

    const onChangePage = (newPage: number) => {
        setQuery({ page: newPage });
        triggerReport({ params: { ...params, page: newPage } });
    };

    return (
        <>
            <AppBar title="Движение по кассе" />

            <Grid container spacing={3} className="no-print">
                <Grid item xs={12}>
                    <Grid container wrap="wrap" spacing={2} alignItems="center">
                        {(isAdmin || isRevisor) && (
                            <>
                                <Grid item lg={3} md={3} sm={6} xs={12}>
                                    <SearchPoints
                                        multiple
                                        value={points as Point[]}
                                        disableCloseOnSelect
                                        getOptionLabel={o => o.name}
                                        onChange={(_, opts) =>
                                            setQuery({ points: opts.map(o => ({ id: o.id, name: o.name })) })
                                        }
                                        disabled={isLoading}
                                        renderInput={props => (
                                            <TextField
                                                {...props}
                                                name="points"
                                                label="Точки"
                                                variant="filled"
                                                size="small"
                                                fullWidth
                                            />
                                        )}
                                    />
                                </Grid>
                                <Grid item lg={3} md={3} sm={6} xs={12}>
                                    <SearchEmployees
                                        multiple
                                        value={employees as Employee[]}
                                        limitTags={1}
                                        disableCloseOnSelect
                                        getOptionLabel={e => `${e.last_name} ${e.name} ${e.father_name}`}
                                        onChange={(_, opts) =>
                                            setQuery({
                                                employees: opts.map(o => ({
                                                    id: o.id,
                                                    name: o.name,
                                                    last_name: o.last_name,
                                                    father_name: o.father_name,
                                                })),
                                            })
                                        }
                                        disabled={isLoading}
                                        renderInput={props => (
                                            <TextField
                                                {...props}
                                                name="employees"
                                                label="Сотрудники"
                                                variant="filled"
                                                size="small"
                                                fullWidth
                                            />
                                        )}
                                    />
                                </Grid>
                            </>
                        )}
                        <Grid item lg={3} md={3} sm={6} xs={12}>
                            <SearchCustomers
                                multiple
                                value={customers as Customer[]}
                                limitTags={1}
                                disableCloseOnSelect
                                getOptionLabel={c =>
                                    `${c.last_name ?? ''} ${c.name} ${c.father_name ?? ''}`.trim()
                                }
                                onChange={(_, opts) =>
                                    setQuery({
                                        customers: opts.map(o => ({
                                            id: o.id,
                                            name: o.name,
                                            last_name: o.last_name,
                                            father_name: o.father_name,
                                        })),
                                    })
                                }
                                disabled={isLoading}
                                renderInput={props => (
                                    <TextField
                                        {...props}
                                        name="customers"
                                        label="Клиенты"
                                        variant="filled"
                                        size="small"
                                        fullWidth
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item lg={3} md={3} sm={6} xs={12}>
                            <DatePicker
                                mask="__.__.____"
                                label="Дата от"
                                value={date_from}
                                maxDate={moment(date_to)}
                                onChange={v =>
                                    setQuery({ date_from: v ? moment(v).toDate() : null })
                                }
                                disabled={isLoading || isFetching}
                                renderInput={params => (
                                    <TextField
                                        {...params}
                                        name="date_from"
                                        variant="filled"
                                        size="small"
                                        fullWidth
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item lg={3} md={3} sm={6} xs={12}>
                            <DatePicker
                                mask="__.__.____"
                                label="Дата до"
                                value={date_to}
                                maxDate={moment()}
                                onChange={v =>
                                    setQuery({ date_to: v ? moment(v).toDate() : moment().toDate() })
                                }
                                disabled={isLoading || isFetching}
                                renderInput={params => (
                                    <TextField
                                        {...params}
                                        name="date_to"
                                        variant="filled"
                                        size="small"
                                        fullWidth
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item lg={3} md={3} sm={6} xs={12}>
                            <LoadingButton
                                loading={isFetching}
                                disabled={isFetching || emptyFilters}
                                onClick={onSearch}
                                variant="contained"
                                size="large"
                                fullWidth
                            >
                                Поиск
                            </LoadingButton>
                        </Grid>
                        {!emptyFilters && (
                            <Grid item lg={3} md={3} sm={6} xs={12}>
                                <DownloadFile
                                    url="/admin/reports/cashbox-operations/excel"
                                    fileName="cashbox-operation.xlsx"
                                    params={params}
                                >
                                    {(onDownload, { isLoading: dlLoading }) => (
                                        <Button
                                            variant="contained"
                                            startIcon={<ChartGrowthFlat />}
                                            size="large"
                                            disabled={isFetching || isLoading || dlLoading}
                                            onClick={onDownload}
                                            fullWidth
                                        >
                                            Экспорт
                                        </Button>
                                    )}
                                </DownloadFile>
                            </Grid>
                        )}
                    </Grid>
                </Grid>

                {emptyFilters ? (
                    <Grid item xs={12}>
                        <Empty text="Выберите фильтр по точкам, сотрудникам или клиентам" />
                    </Grid>
                ) : !hasSearched ? (
                    <Grid item xs={12}>
                        <Empty text="Нажмите «Поиск», чтобы увидеть отчёт" />
                    </Grid>
                ) : (
                    <>
                        <Grid item xs={12}>
                            {loadingSummary ? (
                                <Loader />
                            ) : errorSummary ? (
                                <Error text="Ошибка загрузки сводки" />
                            ) : successSummary && summaryData?.length ? (
                                <CashboxOperationsSummaryTable list={summaryData} />
                            ) : (
                                <Empty text="Сводные данные отсутствуют" />
                            )}
                        </Grid>
                        <Grid item xs={12}>
                            {loadingReport ? (
                                <Loader />
                            ) : errorReport ? (
                                <Error text="Ошибка загрузки отчёта" />
                            ) : successReport && reportData?.data?.length ? (
                                <CashboxOperationsTable
                                    list={reportData}
                                    page={page}
                                    onChangePage={onChangePage}
                                />
                            ) : (
                                <Empty text="Данные отсутствуют" />
                            )}
                        </Grid>
                    </>
                )}
            </Grid>
        </>
    );
};

export default CashboxOperationsReportPage;
